import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { subscribe, unsubscribe } from '../context/events';
import { MSG, NAME } from '../shared';
import { useSocket } from './socket';

function UseProvideEmployee() {
    const { socket } = useSocket();

    const [deviceToken, setDeviceToken] = useState(localStorage.getItem(NAME.app.token));
    const [employee, setEmployee] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [employeesLoaded, setEmployeesLoaded] = useState(false);
    const [isValidDeviceToken, setIsValidDeviceToken] = useState(false);
    const [originalPath, setOriginalPath] = useState('/');
    const [appVersion, setAppVersion] = useState('');

    const signIn = useCallback(
        (data, cb) => {
            socket.emit(MSG.signIn, data, (response) => {
                if (response.status === 200) {
                    localStorage.setItem(NAME.app.token, response.data.token);
                    setDeviceToken(response.data.token);
                    setIsValidDeviceToken(true);
                    setEmployee(response.data.employee);
                    setAppVersion(response.data.appSettings?.appVersion);
                }

                cb(response);
            });
        },
        [socket]
    );

    const signOut = useCallback(() => {
        socket.emit(MSG.signOut, { token: deviceToken });
    }, [deviceToken, socket]);

    const changePassword = useCallback(
        (data, cb) => {
            socket.emit(MSG.changePassword, data, cb);
        },
        [socket]
    );

    const validateToken = useCallback(
        (data, cb) => {
            socket.emit(MSG.validateToken, data, (response) => {
                if (response.status === 200) {
                    setIsValidDeviceToken(true);
                    setEmployee(response.data.employee);
                    setAppVersion(response.data.appSettings?.appVersion);
                } else {
                    setDeviceToken('');
                    setIsValidDeviceToken(false);
                    localStorage.setItem(NAME.app.token, '');
                }

                cb(response);
            });
        },
        [socket]
    );

    const validateUsername = useCallback(
        (data, cb) => {
            socket.emit(MSG.validateUsername, data, cb);
        },
        [socket]
    );

    const createEmployee = useCallback(
        (data, cb) => {
            socket.emit(MSG.create.employee, data, cb);
        },
        [socket]
    );

    const updateEmployee = useCallback(
        (data, cb) => {
            socket.emit(MSG.update.employee, data, cb);
        },
        [socket]
    );

    const reset = () => {
        setAppVersion('');
        setDeviceToken('');
        setEmployee(null);
        setEmployees([]);
        setEmployeesLoaded(false);
        setIsValidDeviceToken('');
        setOriginalPath('/');
        localStorage.setItem(NAME.app.token, '');
    };

    const processEvent = useCallback(
        (event) => {
            const actionListeners = [MSG.signOut, MSG.create.employee, MSG.update.employee];
            const {
                type,
                detail: { status, message, data }
            } = event;

            do {
                if (!actionListeners.includes(type)) {
                    break;
                }

                if (status !== 200) {
                    console.log(`Error for action (${type}) - ${message}`);
                    break;
                }

                if (type === MSG.signOut && data.token === deviceToken) {
                    reset();
                    break;
                }

                if (type === MSG.create.employee) {
                    setEmployees((prev) => {
                        return [...prev, data.employee];
                    });
                    break;
                }

                if (type === MSG.update.employee) {
                    setEmployees((prev) => {
                        return [
                            ...prev.map((p) => {
                                if (p._id === data.employee._id) {
                                    return data.employee;
                                }
                                return p;
                            })
                        ];
                    });

                    if (employee._id === data.employee._id) {
                        if (!data.employee.active) {
                            reset();
                        } else {
                            setEmployee(data.employee);
                        }
                    }
                    break;
                }

                // nothing to see here
            } while (0);
        },
        [deviceToken, employee]
    );

    // Load Employees
    useEffect(() => {
        if (socket && deviceToken && !employeesLoaded) {
            socket.emit(MSG.read.employees, (response) => {
                setEmployees(response.data.employees);
                setEmployeesLoaded(true);
            });
        }
    }, [deviceToken, employeesLoaded, socket]);

    // App Events
    useEffect(() => {
        if (!employee) {
            return;
        }

        subscribe(MSG.signOut, processEvent);
        subscribe(MSG.create.employee, processEvent);
        subscribe(MSG.update.employee, processEvent);

        return () => {
            unsubscribe(MSG.signOut, processEvent);
            unsubscribe(MSG.create.employee, processEvent);
            unsubscribe(MSG.update.employee, processEvent);
        };
    }, [processEvent, employee]);

    // Check for app version updates
    useEffect(() => {
        const iGetVersion = setInterval(() => {
            if (employeesLoaded) {
                socket.emit(MSG.getVersion, {}, (response) => {
                    setAppVersion(response.data.appVersion);
                });
            }
        }, 60000 * 5);

        return () => {
            clearInterval(iGetVersion);
        };
    }, [employeesLoaded, socket, setAppVersion]);

    return {
        // Keep
        appVersion,
        deviceToken,
        employee,
        employees,
        employeesLoaded,
        originalPath,
        isValidDeviceToken,
        UpdatePassword: changePassword,
        CreateEmployee: createEmployee,
        UpdateEmployee: updateEmployee,
        ValidateToken: validateToken,
        ValidateUsername: validateUsername,
        // To Keep?
        SetOriginalPath: setOriginalPath,
        SignIn: signIn,
        SignOut: signOut
    };
}

function ProvideEmployee({ children }) {
    const useProvideEmployee = UseProvideEmployee();

    return (
        <EmployeeContext.Provider value={useProvideEmployee}>{children}</EmployeeContext.Provider>
    );
}

function UseEmployee() {
    return useContext(EmployeeContext);
}

const EmployeeContext = createContext();

export { UseEmployee as useEmployee };

export default ProvideEmployee;
